import { WatchlistFinancials } from '../../data';
import {
  buildDailyChangePosition,
  getHoldingCostBasis,
  getHoldingQuantity,
  resolveDailyChangePercent,
  type DailyChangeWeighting,
} from '../portfolioActionUtils';

const greetings = {
  neg_25: {
    morning: "It's been a bad morning.",
    afternoon: "It's been a bad afternoon.",
    evening: "It's been a bad evening.",
    night: "It's been a bad day.",
  },
  neg_10: {
    morning: 'It has not been a good morning.',
    afternoon: 'It has not been a good afternoon.',
    evening: 'It has not been a good evening.',
    night: 'It has not been a good night.',
  },
  neg_5: {
    morning: "It's not looking great this morning.",
    afternoon: "It's not looking great this afternoon.",
    evening: "It's not looking great this evening.",
    night: "It's not looking great tonight.",
  },
  neg_2: {
    morning: 'Things could be better.',
    afternoon: 'Things could be better.',
    evening: 'Things could be better.',
    night: 'Things could be better.',
  },
  neutral: {
    morning: 'Not much change in overall performance this morning.',
    afternoon: 'Not much change in overall performance this afternoon.',
    evening: 'Not much change in overall performance this evening.',
    night: 'Not much change in overall performance tonight.',
  },
  no_tickers: {
    morning: "Looks like you haven't added any companies to your watchlist.",
    afternoon: "Add some tickers to your watchlist and we'll work our magic.",
    evening: 'Your watchlist is looking kind of empty.',
    night: "It's lonely in here, fill me up with tickers!",
  },
  pos_2: {
    morning: 'Things are looking good this morning.',
    afternoon: 'Things are looking good this afternoon.',
    evening: 'Things are looking good this evening.',
    night: 'Things are looking good tonight.',
  },
  pos_5: {
    morning: 'Good morning! Things are looking great.',
    afternoon: 'Good afternoon! Things are looking great.',
    evening: 'Good evening! Things are looking great.',
    night: 'Good night! Things are looking great.',
  },
  pos_10: {
    morning: 'What an amazing morning!',
    afternoon: 'What an amazing afternoon!',
    evening: 'What an amazing evening!',
    night: 'What an amazing day!',
  },
  pos_25: {
    morning: 'This could be the best day ever!',
    afternoon: 'This could be the best day ever!',
    evening: 'This could be the best day ever!',
    night: 'This could be the best day ever!',
  },
};

const getTimeOfDay = () => {
  const currentHour = new Date().getHours();

  if (currentHour >= 3 && currentHour < 12) {
    return 'morning';
  } else if (currentHour >= 12 && currentHour < 16) {
    return 'afternoon';
  } else if (currentHour >= 16 && currentHour < 20) {
    return 'evening';
  } else {
    return 'night';
  }
};

const getGreeting = (percentage, count) => {
  const timeOfDay = getTimeOfDay();
  switch (true) {
    case count === 0:
      return greetings.no_tickers[timeOfDay];
    case percentage < -25:
      return greetings.neg_25[timeOfDay];
    case percentage > 25:
      return greetings.pos_25[timeOfDay];
    case percentage < -10:
      return greetings.neg_10[timeOfDay];
    case percentage > 10:
      return greetings.pos_10[timeOfDay];
    case percentage < -5:
      return greetings.neg_5[timeOfDay];
    case percentage > 5:
      return greetings.pos_5[timeOfDay];
    case percentage < -2:
      return greetings.neg_2[timeOfDay];
    case percentage > 2:
      return greetings.pos_2[timeOfDay];
    default:
      return greetings.neutral[timeOfDay];
  }
};

export interface WatchlistStats {
  count: number;
  cost: number;
  gain: number;
  gain_percentage: number;
  marketValue: number;
  sorted: WatchlistFinancials[];
  best_ticker: WatchlistFinancials;
  worst_ticker: WatchlistFinancials;
  percentage_avg: number;
  percentage_weighting: DailyChangeWeighting;
  positive_count: number;
  negative_count: number;
  positive_percentage: number;
  negative_percentage: number;
  top_moving_positive: WatchlistFinancials[];
  top_moving_negative: WatchlistFinancials[];
  top_moving_positive_message: string;
  top_moving_negative_message: string;
  greeting: string;
}

export const processWatchlistStats = (tickers: WatchlistFinancials[]): WatchlistStats => {
  const maxTickers = 5;
  const sortedPercentages = tickers
    .filter(ticker => ticker.quote != null && Number.isFinite(ticker.quote.changePercent))
    .sort((a, b) => {
      return (b.quote?.changePercent ?? 0) - (a.quote?.changePercent ?? 0);
    });
  const topMovingPositive = tickers.filter(ticker => {
    return ticker.quote && ticker.quote.changePercent > 5;
  });
  const topMovingNegative = tickers.filter(ticker => {
    return ticker.quote && ticker.quote.changePercent < -5;
  });
  const topMovingPositiveString =
    topMovingPositive.length === 1
      ? topMovingPositive[0].symbol
      : topMovingPositive
          .slice(0, maxTickers + 1)
          .map((e, index) => {
            return index !== topMovingPositive.length - 1 && index !== maxTickers
              ? e.symbol
              : topMovingPositive.length <= index + 1
                ? `and ${e.symbol}`
                : `and ${topMovingPositive.length - index} more tickers`;
          })
          .join(', ');
  const topMovingNegativeString =
    topMovingNegative.length === 1
      ? topMovingNegative[0].symbol
      : topMovingNegative
          .slice(0, maxTickers + 1)
          .map((e, index) => {
            return index !== topMovingNegative.length - 1 && index !== maxTickers
              ? e.symbol
              : topMovingNegative.length <= index + 1
                ? `and ${e.symbol}`
                : `and ${topMovingNegative.length - index} more tickers`;
          })
          .join(', ');
  const positions = tickers.flatMap(ticker => {
    const position = buildDailyChangePosition({
      changePercent: ticker.quote?.changePercent,
      costBasis: getHoldingCostBasis(ticker.holdings),
      lastTradePrice: ticker.quote?.lastTradePrice,
      previousClosePrice: ticker.quote?.previousClosePrice,
      quantity: getHoldingQuantity(ticker.holdings),
      symbol: ticker.symbol,
    });
    return position ? [position] : [];
  });
  const dailyChange = resolveDailyChangePercent(positions);
  const percentageAvg = dailyChange?.percent ?? 0;
  const percentageWeighting = dailyChange?.weighting ?? 'watchlist';
  const winLossSource =
    percentageWeighting === 'portfolio' ? positions.filter(position => position.marketValue > 0) : positions;
  const positiveCount = winLossSource.reduce((total, position) => total + (position.changePercent > 0 ? 1 : 0), 0);
  const negativeCount = winLossSource.reduce((total, position) => total + (position.changePercent < 0 ? 1 : 0), 0);
  const winLossDenominator = winLossSource.length;

  const portfolioCost = tickers.reduce((agg, val) => agg + getHoldingCostBasis(val.holdings), 0);
  const portfolioValue = tickers.reduce((agg, val) => {
    const quantity = getHoldingQuantity(val.holdings);
    const lastTradePrice = val.quote?.lastTradePrice ?? 0;
    const costBasis = getHoldingCostBasis(val.holdings);
    const value = quantity > 0 && lastTradePrice ? quantity * lastTradePrice : costBasis;
    return agg + value;
  }, 0);
  const portfolioGain = portfolioValue - portfolioCost;
  const portfolioGainPercentage = (portfolioGain / portfolioCost) * 100;
  const tickerCount = tickers.length;

  return {
    count: tickerCount,
    cost: portfolioCost,
    gain: portfolioGain,
    gain_percentage: portfolioGainPercentage,
    marketValue: portfolioValue,
    sorted: sortedPercentages,
    best_ticker: sortedPercentages[0],
    worst_ticker: sortedPercentages[sortedPercentages.length - 1],
    percentage_avg: percentageAvg,
    percentage_weighting: percentageWeighting,
    positive_count: positiveCount,
    negative_count: negativeCount,
    positive_percentage: winLossDenominator > 0 ? (positiveCount / winLossDenominator) * 100 : 0,
    negative_percentage: winLossDenominator > 0 ? (negativeCount / winLossDenominator) * 100 : 0,
    top_moving_positive: topMovingPositive,
    top_moving_negative: topMovingNegative,
    top_moving_positive_message: topMovingPositiveString,
    top_moving_negative_message: topMovingNegativeString,
    greeting: getGreeting(percentageAvg, tickerCount),
  };
};
